import { PageHero, SectionHeader, NumberedList } from "@/components/PageChrome";
import CTABanner from "@/components/CTABanner";
import { useSEO } from "@/lib/seo";

const PRINCIPLES = [
    {
        title: "Listen first.",
        body: "Every engagement begins with a real conversation about what you own, who matters to you, and what you actually want the next twenty years to look like. Not a checklist of forms.",
    },
    {
        title: "Answer promptly.",
        body: "Emails and calls are returned within one business day. No months of silence between drafts, no wondering where your matter sits.",
    },
    {
        title: "Charge plainly.",
        body: "Flat fees for most estate, formation, and closing work; written engagement letters for the rest. No hourly surprises on the invoice.",
    },
    {
        title: "Document carefully.",
        body: "Beneficiary designations, deeds, and operating agreements either match, or they don’t. Ours match. Quietly, that is most of the work.",
    },
];

export default function About() {
    useSEO({
        title: "About Chesterton",
        description:
            "Chesterton is a modern law firm focused on estate, real estate, and business matters for landowners, family businesses, and closely-held enterprises.",
        path: "/about"
    });

    return (
        <>
            <PageHero
                testId="about-hero"
                kicker="About the Firm"
                title={<>A small firm. A long view.</>}
                intro="Chesterton is a small firm focused on the four bodies of law that decide what happens to a family’s land, business, and legacy across a lifetime: estate planning, elder law, real property, and closely-held business counsel."
            />

            <section className="py-20 lg:py-28 bg-white border-y border-acre-line">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-5">
                        <div className="flex items-center mb-6">
                            <span className="acre-tick" />
                            <span className="text-[11px] uppercase tracking-[0.28em] text-acre-muted">The Firm</span>
                        </div>
                        <h2 className="font-serif-display text-4xl lg:text-5xl text-acre-secondary leading-tight">
                            Patient, plain-spoken, and prepared.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-6 text-acre-muted text-base lg:text-lg leading-relaxed">
                        <p>
                            We believe most legal trouble is preventable with a careful conversation and a tidy file. So that’s where we start. A first call costs nothing, and you leave knowing whether you actually need to hire a lawyer.
                        </p>
                        <p>
                            When you do, we quote the work clearly, do it carefully, and keep you informed at every step. We are unhurried with the details that matter and decisive with the ones that don’t.
                        </p>
                        <p>
                            The firm draws clients from landowners, farm operators, small-business owners, and the families that built them.
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-20 lg:py-28 bg-acre-bg border-t border-acre-line">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-5">
                        <div className="flex items-center mb-6">
                            <span className="acre-tick" />
                            <span className="text-[11px] uppercase tracking-[0.28em] text-acre-muted">The Name</span>
                        </div>
                        <h2 className="font-serif-display text-4xl lg:text-5xl text-acre-secondary leading-tight">
                            Named for G.K. Chesterton.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-6 text-acre-muted text-base lg:text-lg leading-relaxed">
                        <p>
                            The firm takes its name from G.K. Chesterton, the English writer and Catholic convert who spent a career defending the home, the family, and the ordinary things most worth protecting. That conviction shapes how we practice: your estate plan should serve your family and your faith.
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-20 lg:py-28 bg-white border-t border-acre-line" data-testid="chesterton-quotes">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16">
                    <div className="max-w-3xl mb-14">
                        <div className="flex items-center mb-6">
                            <span className="acre-tick" />
                            <span className="text-[11px] uppercase tracking-[0.28em] text-acre-muted">Favorite Chesterton</span>
                        </div>
                        <h2 className="font-serif-display text-4xl lg:text-5xl text-acre-secondary leading-tight">
                            A few of his lines we keep coming back to.
                        </h2>
                    </div>

                    <ul className="divide-y divide-acre-line border-y border-acre-line">
                        {[
                            {
                                quote: "Tradition means giving votes to the most obscure of all classes, our ancestors. It is the democracy of the dead.",
                                citation: "Orthodoxy (1908), ch. IV, “The Ethics of Elfland.”",
                            },
                            {
                                quote: "The Christian ideal has not been tried and found wanting. It has been found difficult; and left untried.",
                                citation: "What’s Wrong with the World (1910), pt. I, ch. V.",
                            },
                            {
                                quote: "The way to love anything is to realise that it may be lost.",
                                citation: "Tremendous Trifles (1909), “The Advantages of Having One Leg.”",
                            },
                            {
                                quote: "An adventure is only an inconvenience rightly considered. An inconvenience is only an adventure wrongly considered.",
                                citation: "All Things Considered (1908), “On Running After One’s Hat.”",
                            },
                            {
                                quote: "Too much capitalism does not mean too many capitalists, but too few capitalists.",
                                citation: "The Uses of Diversity (1920). A foundational distributist line.",
                            },
                            {
                                quote: "The most extraordinary thing in the world is an ordinary man and an ordinary woman and their ordinary children.",
                                citation: "Widely attributed to Chesterton and likely a later paraphrase; we have not been able to source it in his published works. We love it too much to leave it off the list.",
                            },
                        ].map((q, i) => (
                            <li
                                key={i}
                                data-testid={`chesterton-quote-${i}`}
                                className="grid grid-cols-12 gap-4 lg:gap-8 py-8 lg:py-10"
                            >
                                <div className="col-span-2 lg:col-span-1 font-serif-display text-2xl lg:text-3xl text-acre-secondary leading-none pt-1">
                                    {String(i + 1).padStart(2, "0")}
                                </div>
                                <div className="col-span-10 lg:col-span-11">
                                    <blockquote className="font-serif-display text-2xl lg:text-3xl text-acre-ink leading-snug">
                                        “{q.quote}”
                                    </blockquote>
                                    <div className="mt-4 text-xs uppercase tracking-[0.18em] text-acre-muted not-italic">
                                        — G.K. Chesterton, {q.citation}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="py-20 lg:py-28 bg-acre-bg border-t border-acre-line" data-testid="tradition-section">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16">
                    <div className="max-w-3xl mb-14">
                        <div className="flex items-center mb-6">
                            <span className="acre-tick" />
                            <span className="text-[11px] uppercase tracking-[0.28em] text-acre-muted">The Long View</span>
                        </div>
                        <h2 className="font-serif-display text-4xl lg:text-5xl text-acre-secondary leading-tight">
                            A partnership between the living, the dead, and the not-yet-born.
                        </h2>
                        <p className="mt-8 text-acre-muted text-base lg:text-lg leading-relaxed">
                            Edmund Burke, writing in 1790, described society as “a partnership not only between those who are living, but between those who are living, those who are dead, and those who are to be born.” A century later, G.K. Chesterton put the same thought more sharply: tradition, he wrote, is “the democracy of the dead,” the vote that our ancestors get in the decisions of our own day, cast against the small and arrogant oligarchy of those who merely happen to be walking about. Every plan we draft sits inside that partnership, whether it names it or not.
                        </p>
                        <div className="mt-6 text-xs uppercase tracking-[0.18em] text-acre-muted">
                            — Burke, Reflections on the Revolution in France (1790); Chesterton, Orthodoxy (1908), ch. IV, “The Ethics of Elfland.”
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
                        <div data-testid="tradition-piety" className="bg-white border border-acre-line p-8 lg:p-10">
                            <div className="text-[11px] uppercase tracking-[0.25em] text-acre-secondary mb-4">
                                Familial Piety
                            </div>
                            <h3 className="font-serif-display text-2xl lg:text-3xl text-acre-ink leading-snug">
                                What we owe those who came before, and those who follow.
                            </h3>
                            <p className="mt-5 text-acre-muted leading-relaxed">
                                Long before the state took an interest in inheritance, families were passing down land, name, and craft as an act of honor. Piety, in Aquinas’s older sense, names the duties that no court will enforce but every generation owes: to remember well, to receive well, and to hand on with care. A good estate plan is the legal shape of that duty made concrete.
                            </p>
                            <div className="mt-6 text-xs uppercase tracking-[0.18em] text-acre-muted">
                                — Cf. Aquinas, Summa Theologiae II-II, Q. 80.
                            </div>
                        </div>

                        <div data-testid="tradition-stewardship" className="bg-white border border-acre-line p-8 lg:p-10">
                            <div className="text-[11px] uppercase tracking-[0.25em] text-acre-secondary mb-4">
                                Stewardship
                            </div>
                            <h3 className="font-serif-display text-2xl lg:text-3xl text-acre-ink leading-snug">
                                Everything we hold, we hold in trust.
                            </h3>
                            <p className="mt-5 text-acre-muted leading-relaxed">
                                The gifts of a lifetime are received rather than earned: the land, the business, the savings, even the memory itself. To hold them in trust is to plan for a season past our own. Stewardship is the discipline of drafting today for a family we will not live to see, on behalf of a Giver we cannot repay.
                            </p>
                            <div className="mt-6 text-xs uppercase tracking-[0.18em] text-acre-muted">
                                — Cf. 1 Peter 4:10; 1 Timothy 5:8.
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 lg:py-28 bg-white border-t border-acre-line">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
                    <SectionHeader
                        kicker="Approach"
                        title={<>How the firm works.</>}
                        description="A short list of choices that show up in every engagement. Quiet in isolation, decisive when you add them up."
                    />
                    <NumberedList items={PRINCIPLES} />
                </div>
            </section>

            <CTABanner />
        </>
    );
}
